/**
 * ChartInteractionContext — the shared interaction state every Data Vault
 * chart subscribes to. Backs the ModeBar (H3) and the shared interaction
 * hooks under `./interactions/` (H4) that drive each chart.
 *
 * Three channels:
 *
 *   1. MODE — a piece of state. Charts read it to know which gesture is
 *      currently armed (pan / brush / lasso / select). The ModeBar sets it.
 *
 *   2. VIEWPORT COMMANDS — an imperative event bus. ModeBar zoom-in/zoom-out
 *      and reset are one-shot actions, not modes. They fire commands that
 *      every registered viewport listener responds to. Done via callbacks
 *      instead of reactive state to avoid stale-closure / re-render churn
 *      and to keep commands fire-and-forget.
 *
 * Brush + lasso bounds also flow through this context once those hooks land,
 * but for now they're per-chart local until cross-chart filtering is needed.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type InteractionMode =
  | 'select'
  | 'pan'
  | 'brush'
  | 'lasso';

/**
 * Commands the ModeBar (or any other UI) can fire at every registered chart
 * viewport. Listeners apply them via d3-zoom.
 */
export type ViewportCommand =
  | { type: 'zoom-in' }
  | { type: 'zoom-out' }
  | { type: 'reset' };

export type ViewportListener = (cmd: ViewportCommand) => void;

interface ChartInteractionState {
  /** Current armed gesture mode. Drives brush/lasso behavior in subscribing charts. */
  mode: InteractionMode;
  setMode: (mode: InteractionMode) => void;

  /** One-shot reset counter — bumps on triggerReset(). Some charts watch this for full-state-clear. */
  resetCount: number;
  triggerReset: () => void;

  /** Subscribe to viewport commands. Returns an unsubscribe function. */
  registerViewportListener: (fn: ViewportListener) => () => void;
  /** Fire a viewport command at every registered listener. */
  emitViewportCommand: (cmd: ViewportCommand) => void;
}

const ChartInteractionCtx = createContext<ChartInteractionState | null>(null);

export function ChartInteractionProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<InteractionMode>('select');
  const [resetCount, setResetCount] = useState(0);

  const viewportListenersRef = useRef<Set<ViewportListener>>(new Set());

  const triggerReset = useCallback(() => {
    setResetCount(c => c + 1);
    setMode('select');
  }, []);

  const registerViewportListener = useCallback((fn: ViewportListener) => {
    viewportListenersRef.current.add(fn);
    return () => {
      viewportListenersRef.current.delete(fn);
    };
  }, []);

  const emitViewportCommand = useCallback((cmd: ViewportCommand) => {
    viewportListenersRef.current.forEach(fn => fn(cmd));
  }, []);

  const value = useMemo<ChartInteractionState>(
    () => ({
      mode,
      setMode,
      resetCount,
      triggerReset,
      registerViewportListener,
      emitViewportCommand,
    }),
    [
      mode,
      resetCount,
      triggerReset,
      registerViewportListener,
      emitViewportCommand,
    ],
  );

  return (
    <ChartInteractionCtx.Provider value={value}>
      {children}
    </ChartInteractionCtx.Provider>
  );
}

/**
 * Hook for chart components to subscribe to the interaction state.
 *
 * Returns a no-op default when used outside a provider so charts can render
 * standalone in tests/storybook without crashing.
 */
export function useChartInteractions(): ChartInteractionState {
  const ctx = useContext(ChartInteractionCtx);
  if (ctx) return ctx;
  return {
    mode: 'select',
    setMode: () => {},
    resetCount: 0,
    triggerReset: () => {},
    registerViewportListener: () => () => {},
    emitViewportCommand: () => {},
  };
}
